export interface VideoClockEstimate {
  offsetMs: number; // backend UTC minus browser performance.timeOrigin + now
  uncertaintyMs: number;
  measuredAt: number;
}

export function clockEstimate(
  clientSend: number,
  serverReceive: number,
  serverSend: number,
  clientReceive: number
): VideoClockEstimate | null {
  if (![clientSend, serverReceive, serverSend, clientReceive].every(Number.isFinite)) return null;
  const networkMs = clientReceive - clientSend - (serverSend - serverReceive);
  if (serverSend < serverReceive || clientReceive < clientSend || networkMs < 0 || networkMs > 2000)
    return null;
  return {
    offsetMs: (serverReceive - clientSend + (serverSend - clientReceive)) / 2,
    uncertaintyMs: networkMs / 2,
    measuredAt: clientReceive,
  };
}

export function createVideoClockSync(
  onUpdate: (estimate: VideoClockEstimate | null) => void,
  now = () => performance.timeOrigin + performance.now()
) {
  const pending = new Map<string, number>();
  let timer: ReturnType<typeof setInterval> | null = null;
  let best: VideoClockEstimate | null = null;
  let serial = 0;
  const prefix = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  let send: ((text: string) => boolean | void) | null = null;

  function ping() {
    const t = now();
    for (const [id, sent] of pending) if (t - sent > 2000) pending.delete(id);
    const id = `${prefix}-${++serial}`;
    pending.set(id, t);
    if (
      send?.(
        JSON.stringify({
          event: "video-clock-ping",
          payload: { request_id: id, client_send_ms: t },
        })
      ) === false
    )
      pending.delete(id);
  }

  return {
    start(sender: (text: string) => boolean | void) {
      if (timer) clearInterval(timer);
      pending.clear();
      best = null;
      onUpdate(null);
      send = sender;
      ping();
      // Low traffic, with a rolling best RTT; old estimates expire in the reader.
      timer = setInterval(ping, 1000);
    },
    ingest(payload: Record<string, unknown>) {
      const id = payload.request_id;
      if (typeof id !== "string") return;
      const t0 = pending.get(id);
      if (t0 === undefined || payload.client_send_ms !== t0) return;
      pending.delete(id);
      const t1 = payload.server_receive_ms,
        t2 = payload.server_send_ms;
      if (typeof t1 !== "number" || typeof t2 !== "number") return;
      const estimate = clockEstimate(t0, t1, t2, now());
      if (
        estimate &&
        (!best ||
          estimate.measuredAt - best.measuredAt > 10_000 ||
          estimate.uncertaintyMs <= best.uncertaintyMs)
      ) {
        best = estimate;
        onUpdate(best);
      }
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = null;
      send = null;
      pending.clear();
      best = null;
      onUpdate(null);
    },
  };
}
